import { FC, useState } from 'react';
import { getDeviceDetail } from '@/api/storeManagement/device';
import { useAppContext } from '@/provider/contexts';
import { useTranslation } from 'react-i18next';
import BaseDialog from '../ui/BaseDialog';
import { DeviceDetailResponse } from '@/types/pages/storeManagement/device';
import JobHistoryTable from './JobHistoryTable';
import PreviewImage from './PreviewImage';
import { Box, CircularProgress, Typography } from '@mui/material';

enum VIEW_TYPE {
    AP = 'ap',
    RT = 'rt',
    TAG = 'tag',
}
const TITLE_MAP_CONFIG = {
    [VIEW_TYPE.AP]: 'AP Details & Last Job Status',
    [VIEW_TYPE.RT]: 'Last Job Status',
    [VIEW_TYPE.TAG]: 'Device Preview & Job History',
};
type DeviceDetailDialogProps = {
    open: boolean;
    deviceId: string;
    type: 'ap' | 'rt' | 'tag';
    formatUtcToStoreTime: (utcTime?: string | null) => string;
    onClose: () => void;
};

const DeviceDetailDialog: FC<DeviceDetailDialogProps> = ({ open, deviceId, type, formatUtcToStoreTime, onClose }) => {
    const { t } = useTranslation('common');
    const { setNotification } = useAppContext();
    const [loading, setLoading] = useState(false);
    const [deviceInfo, setDeviceInfo] = useState<Partial<DeviceDetailResponse>>({
        jobs_history: [],
        details: [],
        current_page: 1,
    });

    const details = deviceInfo?.details || [];
    const detail = details.find((item) => item.page_idx === deviceInfo?.current_page) || null;

    const initialize = async () => {
        try {
            setLoading(true);
            const {
                data: { data, isSuccess, message },
            } = await getDeviceDetail(deviceId);

            if (isSuccess) {
                setDeviceInfo(data);
                return;
            }
            throw new Error(message);
        } catch (error) {
            if (error instanceof Error) {
                setNotification({
                    message: t(error.message || 'Failed to fetch device details.'),
                    severity: 'error',
                });
                return;
            }
            console.error(error);
        } finally {
            setLoading(false);
        }
    };
    return (
        <BaseDialog
            open={open}
            dialogTitle={`${t(TITLE_MAP_CONFIG[type])} [ ${deviceId} ]`}
            onClose={onClose}
            maxWidth="lg"
            showTopRightClose
            cancelButtonText=""
            confirmButtonText=""
            TransitionProps={{
                onEnter: initialize,
                onExited: () => {
                    setDeviceInfo({
                        jobs_history: [],
                        details: [],
                        current_page: 1,
                    });
                },
            }}
        >
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                    <CircularProgress size={30} sx={{ mb: 2 }} />
                </Box>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%', alignItems: 'center' }}>
                    {type === VIEW_TYPE.AP && (
                        <Box
                            sx={{
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'end',
                                flexWrap: 'wrap',
                            }}
                        >
                            <Box>
                                <Typography>
                                    {t('Wakeup')}: {deviceInfo?.wakeup_mode || 'N/A'}
                                </Typography>
                                <Typography>
                                    {t('Switch Channel')}: {deviceInfo?.switch_channel_mode || 'N/A'}
                                </Typography>
                                <Typography>
                                    {t('Network Mode')}: {deviceInfo?.network_mode || 'N/A'}
                                </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'right' }}>
                                <Typography>
                                    {t('Current Tree ID')}: {deviceInfo?.current_treeid || 'N/A'}
                                </Typography>
                                <Typography>
                                    {t('Current Channel')}: {deviceInfo?.current_sub_channel || 'N/A'}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                    {type === VIEW_TYPE.TAG && (
                        <>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    border: '1px solid #ccc',
                                }}
                            >
                                <Box
                                    sx={{
                                        width: '100%',
                                        textAlign: 'center',
                                        padding: '5px',
                                        fontSize: '14px',
                                        fontWeight: 'bold',
                                        backgroundColor: '#e0e0e0',
                                        borderBottom: '1px solid #ccc',
                                    }}
                                >
                                    {t('Page 1')}
                                </Box>

                                <PreviewImage
                                    imgPath={detail?.img}
                                    alt={t('Page 1')}
                                    profile={deviceInfo?.profile}
                                    maxWidth={300}
                                    maxHeight={300}
                                />
                            </Box>
                        </>
                    )}
                    <JobHistoryTable
                        jobs={deviceInfo?.jobs_history || []}
                        type={type}
                        profile={deviceInfo?.profile}
                        formatUtcToStoreTime={formatUtcToStoreTime}
                    />
                </Box>
            )}
        </BaseDialog>
    );
};

export default DeviceDetailDialog;
